// Pan/zoom on the world Container plus both coordinate conversions. Raw transform
// (BUILD SPEC D4), no library, so screen<->world stays exact and deterministic.
// `dirty` drives render-on-demand. The Camera owns the single click-vs-pan gate
// (`didPan`) so main's selection and this pan logic can never disagree.

import type { Container } from 'pixi.js';

const MIN_SCALE_FLOOR = 0.15; // absolute floor; the real min is set from the fit-all zoom (see setMinScale)
const MAX_SCALE = 12;
const CLICK_DRAG_THRESHOLD = 5; // px net displacement; below this a gesture is a click, not a pan

export interface Camera {
  dirty: boolean;
  /** True once the current (or most recent) pointer gesture crossed the pan threshold. */
  readonly didPan: boolean;
  apply(world: Container): void;
  panBy(dxScreen: number, dyScreen: number): void;
  zoomAt(sx: number, sy: number, factor: number): void;
  /** Set how far the player may zoom OUT (smallest scale). Kept near the fit-all-islands zoom. */
  setMinScale(min: number): void;
  /** Constrain panning to the island cluster (this world-space box), so the map can't be dragged far into
   *  open ocean. A quarter-viewport of slack is allowed past the edge before it stops. */
  setWorldBounds(minX: number, minY: number, maxX: number, maxY: number): void;
  /** Center the given world point on screen at the target zoom. */
  focusOn(worldX: number, worldY: number, targetScale: number): void;
  screenToWorld(sx: number, sy: number): { x: number; y: number };
  worldToScreen(wx: number, wy: number): { x: number; y: number };
  /** Snapshot / restore the full transform (used to save & restore the view around a replay). */
  getView(): { x: number; y: number; scale: number };
  setView(v: { x: number; y: number; scale: number }): void;
}

export function createCamera(canvas: HTMLCanvasElement): Camera {
  let x = 0;
  let y = 0;
  let scale = 1;
  let minScale = MIN_SCALE_FLOOR;
  let dragging = false;
  let didPan = false;
  let downX = 0;
  let downY = 0;
  let hasBounds = false;
  let bMinX = 0, bMinY = 0, bMaxX = 0, bMaxY = 0; // world-space box of the island cluster (pan limit)

  // keep the island cluster on screen: clamp x/y so the visible world rect stays within the content box,
  // grown by a quarter-viewport of slack (a little open sea past the edge is fine, sailing off into nowhere isn't)
  function clampToBounds(): void {
    if (!hasBounds) return;
    const cw = canvas.clientWidth || canvas.width;
    const ch = canvas.clientHeight || canvas.height;
    const slackX = (cw / scale) * 0.25;
    const slackY = (ch / scale) * 0.25;
    const minX = bMinX - slackX, maxX = bMaxX + slackX;
    const minY = bMinY - slackY, maxY = bMaxY + slackY;
    // visible world x-range is [-x/scale, (cw-x)/scale]; keep it inside [minX, maxX] (centre if it can't fit)
    const loX = cw - maxX * scale, hiX = -minX * scale;
    x = loX > hiX ? cw / 2 - ((minX + maxX) / 2) * scale : Math.min(hiX, Math.max(loX, x));
    const loY = ch - maxY * scale, hiY = -minY * scale;
    y = loY > hiY ? ch / 2 - ((minY + maxY) / 2) * scale : Math.min(hiY, Math.max(loY, y));
  }

  const cam: Camera = {
    dirty: true,
    get didPan() {
      return didPan;
    },
    apply(world) {
      world.position.set(x, y);
      world.scale.set(scale);
    },
    getView() {
      return { x, y, scale };
    },
    setView(v) {
      x = v.x;
      y = v.y;
      scale = v.scale;
      cam.dirty = true;
    },
    panBy(dx, dy) {
      x += dx;
      y += dy;
      clampToBounds();
      cam.dirty = true;
    },
    setMinScale(min) {
      minScale = Math.max(MIN_SCALE_FLOOR, min);
      if (scale < minScale) {
        scale = minScale;
        cam.dirty = true;
      }
    },
    setWorldBounds(minX, minY, maxX, maxY) {
      bMinX = minX;
      bMinY = minY;
      bMaxX = maxX;
      bMaxY = maxY;
      hasBounds = true;
      clampToBounds();
      cam.dirty = true;
    },
    zoomAt(sx, sy, factor) {
      const next = Math.min(MAX_SCALE, Math.max(minScale, scale * factor));
      if (next === scale) return;
      const wx = (sx - x) / scale;
      const wy = (sy - y) / scale;
      scale = next;
      x = sx - wx * scale;
      y = sy - wy * scale;
      clampToBounds();
      cam.dirty = true;
    },
    focusOn(worldX, worldY, targetScale) {
      scale = Math.min(MAX_SCALE, Math.max(minScale, targetScale));
      const cw = canvas.clientWidth || canvas.width;
      const ch = canvas.clientHeight || canvas.height;
      x = cw / 2 - worldX * scale;
      y = ch / 2 - worldY * scale;
      cam.dirty = true;
    },
    screenToWorld(sx, sy) {
      return { x: (sx - x) / scale, y: (sy - y) / scale };
    },
    worldToScreen(wx, wy) {
      return { x: wx * scale + x, y: wy * scale + y };
    },
  };

  // middle-button mousedown triggers browser autoscroll, suppress it on the map
  canvas.addEventListener('mousedown', (e) => {
    if (e.button === 1) e.preventDefault();
  });
  canvas.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 && e.button !== 1) return; // left OR middle button pans
    dragging = true;
    didPan = e.button === 1; // middle button pans immediately (no click-vs-pan threshold)
    downX = e.offsetX;
    downY = e.offsetY;
    // Capture so the release is delivered even if the pointer leaves the window
    // mid-drag (prevents a stuck drag inside the CrazyGames iframe).
    canvas.setPointerCapture(e.pointerId);
  });

  const endDrag = (): void => {
    dragging = false;
  };
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  canvas.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    if ((e.buttons & 1) === 0 && (e.buttons & 4) === 0) {
      // neither left nor middle button held (e.g. released off-window), self-heal
      dragging = false;
      return;
    }
    if (!didPan && Math.hypot(e.offsetX - downX, e.offsetY - downY) > CLICK_DRAG_THRESHOLD) {
      didPan = true;
    }
    if (didPan) cam.panBy(e.movementX, e.movementY);
  });

  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      cam.zoomAt(e.offsetX, e.offsetY, factor);
    },
    { passive: false },
  );

  return cam;
}
